import { z } from 'zod';
import { IsoDateTime } from '../ingest/common.ts';

/**
 * Event export (BB-137, AN-010): message facts leave the service as immutable gzip JSON Lines files in closed UTC
 * hours by `recorded_at`, one manifest per window as its commit marker. A connector writes to an S3-compatible bucket
 * (analytics or a compliance archive); Mixpanel is reserved until its decision (T6). Secrets never pass the API: a
 * connector stores only a reference (`env:NAME`, `file:/path`) that the service resolves.
 */
export const EVENT_EXPORT_KINDS = ['s3', 'mixpanel'] as const;
export type EventExportKind = (typeof EVENT_EXPORT_KINDS)[number];
export const EVENT_EXPORT_PURPOSES = ['analytics', 'archive'] as const;
export type EventExportPurpose = (typeof EVENT_EXPORT_PURPOSES)[number];
export const EVENT_EXPORT_DATASETS = ['message_fact'] as const;
export type EventExportDataset = (typeof EVENT_EXPORT_DATASETS)[number];
/** Which identifiers a record carries: none, the internal profile and device ids, or also the external ID. */
export const IDENTIFIER_MODES = ['none', 'profile_id', 'external_id'] as const;
export type IdentifierMode = (typeof IDENTIFIER_MODES)[number];
/** How the sink makes a write put-if-absent; found by the connection test (probe). */
export const CONDITIONAL_WRITES = ['if_none_match', 'head_then_put', 'unsupported'] as const;
export type ConditionalWrite = (typeof CONDITIONAL_WRITES)[number];
export const CONNECTOR_STATUSES = ['ok', 'lagging', 'failing', 'disabled', 'archived'] as const;
export type ConnectorStatus = (typeof CONNECTOR_STATUSES)[number];
export const RECONCILE_STATUSES = ['ok', 'missing', 'late_facts', 'shrunk', 'pending'] as const;
export type ReconcileStatus = (typeof RECONCILE_STATUSES)[number];

export const RECORD_SCHEMA_VERSION = 1;
export const MANIFEST_VERSION = 1;
export const REPLAY_MAX_DAYS = 31;
/** Consecutive failed runs after which a connector shows as failing. */
export const FAILING_AFTER = 5;

/** `env:NAME` (a variable of the service) or `file:/path` (inside `BB_SECRETS_DIR`); `store:` arrives with BB-160. */
export const SecretRef = z.string().regex(/^(env:[A-Z][A-Z0-9_]{0,127}|file:\/[^\0]{1,1024})$/, 'env:NAME or file:/path');
const FactTypes = z.array(z.string().regex(/^[a-z][a-z_]{1,63}$/)).min(1).max(100);

/** Object key prefix: no leading slash and no `..` segment. */
const Prefix = z
  .string()
  .max(512)
  .regex(/^[A-Za-z0-9!_.*'()/-]*$/, 'letters, digits and !_.*\'()/- only')
  .refine((p) => !p.startsWith('/') && !p.split('/').includes('..'), 'no leading slash and no .. segment');

export const S3Config = z.strictObject({
  /** A custom S3-compatible endpoint (MinIO, SeaweedFS, an on-premises store); AWS when absent. */
  endpoint: z.url({ protocol: /^https?$/ }).optional(),
  region: z.string().trim().min(1).max(64),
  bucket: z.string().regex(/^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/, 'an S3 bucket name'),
  prefix: Prefix.default(''),
  forcePathStyle: z.boolean().default(false),
  sse: z.strictObject({ mode: z.enum(['AES256', 'aws:kms']), kmsKeyId: z.string().min(1).max(2048).optional() }).optional(),
  /** Object Lock retention of every data object (parts and manifests); the bucket must have Object Lock enabled. */
  objectLock: z.strictObject({ mode: z.enum(['GOVERNANCE', 'COMPLIANCE']), retainDays: z.number().int().min(1).max(36_500) }).optional(),
});
export type S3Config = z.infer<typeof S3Config>;

/** Reserved for T6 (blocked on decision): the service refuses to create such a connector for now. */
export const MixpanelConfig = z.strictObject({ baseUrl: z.url({ protocol: /^https$/ }).default('https://api-eu.mixpanel.com'), projectId: z.string().trim().min(1).max(64) });
export type MixpanelConfig = z.infer<typeof MixpanelConfig>;

/** S3 credentials as the secret holds them (JSON). */
export const S3Credentials = z.object({ accessKeyId: z.string().min(1), secretAccessKey: z.string().min(1), sessionToken: z.string().min(1).optional() });
export type S3Credentials = z.infer<typeof S3Credentials>;

const common = {
  name: z.string().trim().min(1).max(120),
  secretRef: SecretRef,
  datasets: z.array(z.enum(EVENT_EXPORT_DATASETS)).min(1).default(['message_fact']),
  /** Fact types to export; null = every type. */
  factTypes: FactTypes.nullable().default(null),
  identifierMode: z.enum(IDENTIFIER_MODES).default('profile_id'),
  /** Required with `external_id`: archived files keep the external ID after a privacy erase. */
  acknowledgeErasureConflict: z.boolean().optional(),
  enabled: z.boolean().default(true),
  /** First window to export (rounded down to the hour); default: the start of the current hour. History goes through replay. */
  startFrom: IsoDateTime.optional(),
};

export const CreateConnectorBody = z
  .discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('s3'), purpose: z.enum(EVENT_EXPORT_PURPOSES), config: S3Config, ...common }),
    z.strictObject({ kind: z.literal('mixpanel'), purpose: z.literal('analytics'), config: MixpanelConfig, ...common }),
  ])
  .refine((c) => c.identifierMode !== 'external_id' || c.acknowledgeErasureConflict === true, {
    message: 'external_id in exported files survives a privacy erase; confirm with acknowledgeErasureConflict',
    path: ['acknowledgeErasureConflict'],
  });
export type CreateConnectorBody = z.infer<typeof CreateConnectorBody>;

/** `kind`, `purpose`, `datasets` and `identifierMode` are locked once a window was written (`409 connector_locked`). */
export const UpdateConnectorBody = z
  .strictObject({
    name: common.name.optional(),
    purpose: z.enum(EVENT_EXPORT_PURPOSES).optional(),
    config: z.unknown().optional(),
    secretRef: SecretRef.optional(),
    factTypes: FactTypes.nullable().optional(),
    identifierMode: z.enum(IDENTIFIER_MODES).optional(),
    acknowledgeErasureConflict: z.boolean().optional(),
    enabled: z.boolean().optional(),
  })
  .refine((c) => c.identifierMode !== 'external_id' || c.acknowledgeErasureConflict === true, {
    message: 'external_id in exported files survives a privacy erase; confirm with acknowledgeErasureConflict',
    path: ['acknowledgeErasureConflict'],
  });
export type UpdateConnectorBody = z.infer<typeof UpdateConnectorBody>;

export const ConnectorView = z.object({
  id: z.uuid(),
  environmentId: z.uuid(),
  name: z.string(),
  kind: z.enum(EVENT_EXPORT_KINDS),
  purpose: z.enum(EVENT_EXPORT_PURPOSES),
  config: z.record(z.string(), z.unknown()),
  secretRef: z.string(),
  datasets: z.array(z.enum(EVENT_EXPORT_DATASETS)),
  factTypes: z.array(z.string()).nullable(),
  identifierMode: z.enum(IDENTIFIER_MODES),
  enabled: z.boolean(),
  status: z.enum(CONNECTOR_STATUSES),
  /** Every window before this moment is exported. */
  exportedUntil: IsoDateTime,
  /** Seconds the export is behind the newest window it could have written. */
  lagSeconds: z.number().int(),
  conditionalWrite: z.enum(CONDITIONAL_WRITES).nullable(),
  locked: z.boolean(),
  lastRunAt: IsoDateTime.nullable(),
  lastSuccessAt: IsoDateTime.nullable(),
  lastError: z.string().nullable(),
  consecutiveFailures: z.number().int(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  archivedAt: IsoDateTime.nullable(),
});
export type ConnectorView = z.infer<typeof ConnectorView>;

export const TestConnectionResult = z.object({
  ok: z.boolean(),
  conditionalWrite: z.enum(CONDITIONAL_WRITES).nullable(),
  /** `secret_missing`, `secret_invalid`, `auth_failed`, `bucket_not_found`, `sink_unreachable`, … */
  code: z.string().optional(),
  message: z.string().optional(),
});
export type TestConnectionResult = z.infer<typeof TestConnectionResult>;

/** Whole UTC hours: `from` and `to` are rounded down to the hour; at most 31 days. */
export const ReplayBody = z.strictObject({ from: IsoDateTime, to: IsoDateTime });
export type ReplayBody = z.infer<typeof ReplayBody>;

export const ExportPart = z.object({ key: z.string(), rows: z.number().int().min(0), bytes: z.number().int().min(0), contentSha256: z.string().regex(/^[0-9a-f]{64}$/) });
export type ExportPart = z.infer<typeof ExportPart>;

export const WindowView = z.object({
  dataset: z.enum(EVENT_EXPORT_DATASETS),
  windowStart: IsoDateTime,
  windowEnd: IsoDateTime,
  version: z.number().int().min(1),
  rowCount: z.number().int(),
  rowsByType: z.record(z.string(), z.number().int()),
  parts: z.array(ExportPart),
  manifestKey: z.string(),
  writtenAt: IsoDateTime,
});
export type WindowView = z.infer<typeof WindowView>;

export const WindowsPage = z.object({ items: z.array(WindowView), nextCursor: z.string().nullable() });
export type WindowsPage = z.infer<typeof WindowsPage>;

const Counts = z.object({ rows: z.number().int(), byType: z.record(z.string(), z.number().int()) });
export const ReconcileView = z.object({
  date: z.iso.date(),
  connectorId: z.uuid(),
  windows: z.array(z.object({ windowStart: IsoDateTime, status: z.enum(RECONCILE_STATUSES), db: Counts, exported: Counts.extend({ version: z.number().int() }).nullable() })),
  totals: z.object({ db: Counts, exported: Counts }),
  /** Every closed window of the day is `ok` or `shrunk`. */
  ok: z.boolean(),
});
export type ReconcileView = z.infer<typeof ReconcileView>;

/**
 * One line of a `message_fact` export file, schema 1. Fields are always present (null when empty) so warehouse
 * columns stay stable; new fields may be added (non-breaking), so readers must ignore unknown ones.
 */
export const ExportRecordV1 = z.object({
  id: z.uuid(),
  occurredAt: IsoDateTime,
  recordedAt: IsoDateTime,
  profileId: z.uuid().nullable(),
  externalId: z.string().nullable(),
  deviceId: z.uuid().nullable(),
  source: z.string(),
  campaignId: z.uuid().nullable(),
  canvasId: z.uuid().nullable(),
  canvasStepId: z.uuid().nullable(),
  variantApiId: z.string().nullable(),
  dispatchId: z.uuid().nullable(),
  channel: z.string().nullable(),
  platform: z.string().nullable(),
  locale: z.string().nullable(),
  type: z.string(),
  reason: z.string().nullable(),
  attrs: z.record(z.string(), z.unknown()),
  isControl: z.boolean(),
  cohortDay: z.iso.date(),
});
export type ExportRecordV1 = z.infer<typeof ExportRecordV1>;

/**
 * `_manifest-v<V>.json`, written last: the window is complete only when its manifest exists. A reader takes the
 * highest manifest version of a window, reads exactly its parts and still dedupes by record `id`.
 */
export const ManifestV1 = z.object({
  manifestVersion: z.literal(MANIFEST_VERSION),
  dataset: z.enum(EVENT_EXPORT_DATASETS),
  schemaVersion: z.literal(RECORD_SCHEMA_VERSION),
  workspace: z.string(),
  environment: z.string(),
  window: z.object({ from: IsoDateTime, to: IsoDateTime, basis: z.literal('recorded_at') }),
  version: z.number().int().min(1),
  supersedes: z.number().int().min(1).nullable(),
  generatedAt: IsoDateTime,
  identifierMode: z.enum(IDENTIFIER_MODES),
  factTypes: z.array(z.string()),
  rowCount: z.number().int().min(0),
  rowsByType: z.record(z.string(), z.number().int()),
  /** `contentSha256` is the SHA-256 of the uncompressed JSON Lines (also the object's `content-sha256` metadata). */
  parts: z.array(ExportPart),
});
export type ManifestV1 = z.infer<typeof ManifestV1>;
